import { OrthoFlight } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateOrthoFlightArgs,
    getDefaultOrthoFlightCreateArgs,
    UpdateOrthoFlightArgs,
} from "@nova/serializer/src/client/validators/flights/ortho"
import {
    selectOrthoFlightById,
    selectOrthoFlights,
} from "features/entity-caching/flights/selectors"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { setFlightId } from "features/map/control/slice"
import { deleteUploadSet } from "plugins/Orthomosaic/OrthomosaicSlice"
import { updateIntegrationMetadata } from "features/pubsub/module"
import { SampleDataFlightKey } from "@nova/constants"
import {
    selectSingleLayer,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

const {
    create: createOrthoFlight,
    update: updateOrthoFlight,
    delete: deleteOrthoFlight,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    OrthoFlight, // entity type
    CreateOrthoFlightArgs, // create args to send to server
    ["createdBy"], // create args required by action
    UpdateOrthoFlightArgs, // update args
    { updateIntegrationId?: string; sampleDataFlightKey?: SampleDataFlightKey }, // required input context
    { sampleDataFlightKey?: SampleDataFlightKey }
>("OrthoFlight")

export const addOrthoFlightsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const flights = selectOrthoFlights(state)
            const name = `Orthomosaic ${Object.values(flights).length + 1}`
            const id = randomUUID()
            const args = {
                name,
                id,
                demoId: !!context?.sampleDataFlightKey ? "doesnt-matter" : undefined,
            }
            const optimisticData = {
                ...args,
                ...getDefaultOrthoFlightCreateArgs(state.project.data),
                ...data,
            }
            createOrthoFlight(
                socket,
                { ...args, ...data },
                { sampleDataFlightKey: context?.sampleDataFlightKey }
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["orthoFlights"],
                })
            )

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
            store.dispatch(setFlightId(id))
            store.dispatch(setActiveLayerId({ id: optimisticData.id }))
            store.dispatch(setActiveFeatureId({ feature: NovaPluginIds.Ortho }))
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateOrthoFlight(socket, id, updates, { ...context })

            if (context?.updateIntegrationId) {
                const state = store.getState()
                const entity = selectOrthoFlightById(id)(state)!
                if (entity) {
                    updates.integrations = updateIntegrationMetadata(
                        entity.integrations,
                        context.updateIntegrationId
                    )
                }
            }

            if (context?.sampleDataFlightKey) updates.demoId = "doesnt-matter"

            store.dispatch(updateEntitySocket({ updates, path: ["orthoFlights", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            const dispatch = store.dispatch
            dispatch(deleteUploadSet({ flightId: id }))
            deleteOrthoFlight(socket, id, {})
            dispatch(deleteEntitySocket({ path: ["orthoFlights", { id }] }))
        })
export {
    createAction as createOrthoFlight,
    updateAction as updateOrthoFlight,
    deleteAction as deleteOrthoFlight,
}
